"use client";

import React, { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, Clock, ShieldCheck, HardHat, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function RequestService() {
  const shouldReduceMotion = useReducedMotion();
  useEffect(() => {
    const linkId = "jobber-work-request-css";
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.media = "screen";
      link.href = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/external/work_request_embed.css";
      document.head.appendChild(link);
    }
    const scriptId = "jobber-work-request-js";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://d3ey4dbjkt2f6s.cloudfront.net/assets/static_link/work_request_embed_snippet.js";
    script.setAttribute("clienthub_id", "476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587");
    script.setAttribute("form_url", "https://clienthub.getjobber.com/client_hubs/476c5fe2-f16a-4108-ad33-1763e02c3dad/public/work_request/embedded_work_request_form?form_id=4972587");
    script.async = true;
    document.body.appendChild(script);
  }, []);
  return <section id="request-service" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground transition-colors">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Topbar */}
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
              <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
              Direct Dispatch
            </span>
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-mono">
              Regional Command: MD • DC • Northern VA
            </span>
          </div>
          <div className="text-xs text-muted-foreground font-medium">
            Joint Mitigation-to-Rebuild Scoping Available 24/7
          </div>
        </div>

        {/* Section Header */}
        <div className="mb-12 max-w-3xl">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Request rebuild and design-build service
          </h2>
          <p className="mt-3 text-lg text-muted-foreground">
            Connect directly with our local regional project managers. We scope framing, structural
            mitigation, and interior restoration under one continuous contract without outsourced call
            centers.
          </p>
        </div>

        {/* Main Grid: Form + Consultation Card */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          {/* Left Column: Form Container */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.32,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <Card className="border border-border bg-card shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <div id="476c5fe2-f16a-4108-ad33-1763e02c3dad-4972587" />
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Direct Consultation & Joint Scoping Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.32,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 space-y-6">
            {/* Dark Purple Institutional Consultation Card */}
            <div className="rounded-xl border border-secondary/20 bg-secondary p-6 sm:p-8 text-secondary-foreground shadow-md">
              <div className="inline-flex items-center gap-2 rounded-sm bg-accent/20 px-2.5 py-1 text-xs font-bold text-accent uppercase tracking-wider">
                <Clock className="size-3.5" />
                Immediate Phone Dispatch
              </div>

              <h3 className="mt-4 text-2xl font-bold tracking-tight text-secondary-foreground">
                Speak directly with an on-duty project manager
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-secondary-foreground/80">
                Skip call centers and automated phone trees. Connect straight to licensed field teams
                handling emergency containment and turnkey rebuilds across MD, DC, and Northern Virginia.
              </p>

              <div className="mt-6 border-t border-secondary-foreground/15 pt-6">
                <div className="text-xs uppercase tracking-wider text-secondary-foreground/70 font-mono">
                  Direct Emergency Line
                </div>
                <div className="mt-1">
                  <a href="tel:855-435-6631" className="inline-flex items-center gap-2.5 text-2xl font-extrabold tracking-tight text-accent hover:underline font-mono">
                    <Phone className="size-6" />
                    855-435-6631
                  </a>
                </div>
                <p className="mt-1 text-xs text-secondary-foreground/70">
                  Available 24 hours a day, 7 days a week, 365 days a year.
                </p>
              </div>

              <div className="mt-6">
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11">
                  <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                    <Phone className="size-4" />
                    Call Regional Dispatch Now
                  </a>
                </Button>
              </div>
            </div>

            {/* Joint Mitigation-to-Rebuild Scoping Specs */}
            <Card className="border border-border bg-card shadow-xs">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 font-semibold text-foreground text-sm uppercase tracking-wider font-mono">
                  <HardHat className="size-4 text-primary" />
                  Joint Scoping Benefits
                </div>

                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <ShieldCheck className="size-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Single contractor accountability</h4>
                      <p className="text-xs leading-relaxed text-muted-foreground mt-0.5">
                        No disputes between your tear-out crew and rebuild contractor. We handle framing,
                        permitting, electrical, and finish carpentry in continuity.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <FileText className="size-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Direct insurance adjuster sync</h4>
                      <p className="text-xs leading-relaxed text-muted-foreground mt-0.5">
                        We document moisture levels, structural framing integrity, and material specs in Xactimate
                        to support seamless claims authorization.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 rounded-md bg-muted/60 p-3.5 border border-border/80">
                  <div className="text-xs font-medium text-foreground">
                    Serving residential, commercial & HOA properties
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Licensed & insured in Maryland, District of Columbia, and Virginia.
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}